import { EppoClient, IAssignmentEvent } from '@eppo/js-client-sdk-common'
import { DefaultSubjectAttributes } from '../../shared/DefaultSubjectAttributes'

type Attributes = {
  [key: string]: string | number | boolean
}

export type GetExperimentVariantParams = {
  subjectAttributes?: Attributes
}

export type GetExperiment = <R>(
  experimentKey: string,
  args: { fallback: R; subjectAttributes?: Attributes },
) => R

export const setupGetExperimentVariant = ({
  client,
  subjectKey,
  defaultAttributes,
  overrides,
}: {
  client: EppoClient | null
  subjectKey: string
  defaultAttributes: DefaultSubjectAttributes
  overrides: { [key: string]: string }
}) => {
  const trackedServerExperiments: Record<
    string,
    string | boolean | number | object
  > = {}

  client?.setAssignmentLogger({
    logAssignment: (assignmentEvent: IAssignmentEvent) => {
      if (!assignmentEvent.experiment) return
      if (!assignmentEvent.variation) return
      if (assignmentEvent.subjectAttributes?.shouldLog === false) return
      trackedServerExperiments[assignmentEvent.experiment] =
        assignmentEvent.variation
    },
  })

  const getExperimentString: GetExperiment = (
    experimentKey: string,
    { fallback, subjectAttributes },
  ) => {
    if (!client) return fallback
    if (overrides[experimentKey])
      return overrides[experimentKey] as unknown as typeof fallback

    return client.getStringAssignment(
      experimentKey,
      subjectKey,
      { ...defaultAttributes, ...subjectAttributes },
      fallback as unknown as string,
    ) as unknown as typeof fallback
  }

  const getExperimentBoolean: GetExperiment = (
    experimentKey: string,
    { fallback, subjectAttributes },
  ) => {
    if (!client) return fallback
    if (overrides[experimentKey])
      return (overrides[experimentKey] === 'true') as unknown as typeof fallback

    return client.getBooleanAssignment(
      experimentKey,
      subjectKey,
      { ...defaultAttributes, ...subjectAttributes },
      fallback as unknown as boolean,
    ) as unknown as typeof fallback
  }

  const getExperimentJson: GetExperiment = (
    experimentKey: string,
    { fallback, subjectAttributes },
  ) => {
    if (!client) return fallback
    if (overrides[experimentKey])
      return JSON.parse(overrides[experimentKey]) as unknown as typeof fallback

    return client.getJSONAssignment(
      experimentKey,
      subjectKey,
      { ...defaultAttributes, ...subjectAttributes },
      fallback as unknown as object,
    ) as unknown as typeof fallback
  }

  const getExperimentInteger: GetExperiment = (
    experimentKey: string,
    { fallback, subjectAttributes },
  ) => {
    if (!client) return fallback
    if (overrides[experimentKey])
      return Number(overrides[experimentKey]) as unknown as typeof fallback

    return client.getIntegerAssignment(
      experimentKey,
      subjectKey,
      { ...defaultAttributes, ...subjectAttributes },
      fallback as unknown as number,
    ) as unknown as typeof fallback
  }

  const getExperimentNumeric: GetExperiment = (
    experimentKey: string,
    { fallback, subjectAttributes },
  ) => {
    if (!client) return fallback
    if (overrides[experimentKey])
      return Number(overrides[experimentKey]) as unknown as typeof fallback

    return client.getNumericAssignment(
      experimentKey,
      subjectKey,
      { ...defaultAttributes, ...subjectAttributes },
      fallback as unknown as number,
    ) as unknown as typeof fallback
  }

  return {
    getExperimentString,
    getExperimentBoolean,
    getExperimentJson,
    getExperimentInteger,
    getExperimentNumeric,
    trackedServerExperiments,
  }
}
